import {isClass} from "@cm/utils"
import {concatPath} from "#graph/helpers/path"
import {isBaseGraphNodeInstance} from "#graph/nodes/base/helpers"
import type {NodeParameters} from "#graph/nodes/base/types"
import type {ParameterDifferences} from "#graph/diffing/types"

export const graphParametersDifferences = <ParamTypes extends NodeParameters>(
    parametersOld: ParamTypes,
    parametersNew: ParamTypes,
    checkOnlyGraphInstanceIdEquality = false,
) => {
    const traverseParameters = (oldValue: unknown, newValue: unknown, path: string): ParameterDifferences => {
        const difference = {path, oldValue, newValue}
        if (isBaseGraphNodeInstance(oldValue)) {
            if (!isBaseGraphNodeInstance(newValue)) return [difference]
            else if (checkOnlyGraphInstanceIdEquality) return oldValue.instanceId === newValue.instanceId ? [] : [difference]
            else return oldValue === newValue ? [] : [difference]
        } else if (oldValue instanceof Array) {
            if (!(newValue instanceof Array)) return [difference]
            const maxLength = Math.max(oldValue.length, newValue.length)
            const itemDifferences = [...Array(maxLength).keys()]
                .map((index) => traverseParameters(oldValue[index], newValue[index], concatPath(path, index)))
                .flat()
            if (oldValue.length !== newValue.length) {
                const lengthDifference = {path: concatPath(path, "length"), oldValue: oldValue.length, newValue: newValue.length}
                return [lengthDifference, ...itemDifferences]
            } else return itemDifferences
        } else if (typeof oldValue === "object" && oldValue) {
            if (!(typeof newValue === "object" && newValue)) return [difference]

            if (isClass(oldValue)) {
                if (!isClass(newValue)) return [difference]
                return oldValue === newValue ? [] : [difference]
            } else {
                const keysA = Object.keys(oldValue)
                const keysB = Object.keys(newValue)
                const allKeys = [...new Set([...keysA, ...keysB])]
                return allKeys
                    .map((key) => traverseParameters((oldValue as NodeParameters)[key], (newValue as NodeParameters)[key], concatPath(path, key)))
                    .flat()
            }
        } else return oldValue === newValue ? [] : [difference]
    }

    return traverseParameters(parametersOld, parametersNew, "")
}
